import { ExpandOutlined, InfoCircleTwoTone, ShrinkOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Drawer } from "antd";
import { BreadcrumbItemType } from "antd/es/breadcrumb/Breadcrumb";
import mergeClassNames from "common/mergeClassNames";
import ErrorBoundary from "components/atoms/ErrorBoundary";
import React, { useState } from "react";
import { Link } from "react-router-dom";

// Custom sparkles icon for AI button
const SparklesIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
        <path d="M20 3v4M22 5h-4" />
    </svg>
);

const AssistantSparkleIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill="currentColor"
        />
        <path d="M19 14L19.75 16.25L22 17L19.75 17.75L19 20L18.25 17.75L16 17L18.25 16.25L19 14Z" fill="currentColor" />
    </svg>
);

interface IPageLayoutProps {
    mainContent: (props: IPageLayoutMainContentProps) => React.ReactNode;
    helpContent?: React.ReactNode;
    askAIContent?: React.ReactNode;
    askAITitle?: React.ReactNode;
    askAIWrapper?: React.ComponentType<{ children: React.ReactNode }>;
    assistantContent?: React.ReactNode;
    assistantTitle?: React.ReactNode;
    assistantOnClose?: () => void;
    border?: boolean;
    className?: string;
    actions?: React.ReactNode; // More actions in the header
    actionPlacement?: "left" | "right";
}

export default function PageLayout({
    mainContent,
    helpContent,
    askAIContent,
    askAITitle,
    askAIWrapper: AskAIWrapper,
    assistantContent,
    assistantTitle,
    assistantOnClose,
    border = true,
    className,
    actions,
    actionPlacement = "left",
}: IPageLayoutProps) {
    const [breadcrumbNavigationItems, setBreadcrumbNavigationItems] = useState<IBreadcrumbItem[]>([]);

    const [showDrawer, setShowDrawer] = React.useState<boolean>(false);
    const [showChatDrawer, setShowChatDrawer] = React.useState<boolean>(false);
    const [showAssistantDrawer, setShowAssistantDrawer] = React.useState<boolean>(false);
    const [assistantExpanded, setAssistantExpanded] = React.useState<boolean>(false);

    return (
        <div id="page-layout-new" className={mergeClassNames("flex flex-1 flex-col gap-2 overflow-auto", className)}>
            <div className="bread-crumbs-container flex min-h-7 w-full flex-row items-center">
                <div className="flex flex-1 items-center gap-2">
                    {breadcrumbNavigationItems.length > 0 && (
                        <Breadcrumb
                            className="breadcrumb-item"
                            separator=">"
                            items={breadcrumbNavigationItems.reduce((acc, current) => {
                                acc.push({
                                    path: current.route,
                                    title: current.text,
                                });
                                return acc;
                            }, Array<BreadcrumbItemType>())}
                            itemRender={(currentRoute, _, items) => {
                                const isLast = currentRoute?.path === items[items.length - 1]?.path;
                                return isLast || !currentRoute.path ? <span>{currentRoute.title}</span> : <Link to={currentRoute.path}>{currentRoute.title}</Link>;
                            }}
                        />
                    )}
                    {!!actions && actionPlacement === "left" ? actions : null}
                </div>
                <div className="flex flex-none gap-2">
                    {!!actions && actionPlacement === "right" ? actions : null}
                    {assistantContent && (
                        <Button onClick={() => setShowAssistantDrawer(true)} size="small" icon={<AssistantSparkleIcon />}>
                            Assistant
                        </Button>
                    )}
                    {askAIContent && (
                        <Button onClick={() => setShowChatDrawer(true)} size="small" icon={<SparklesIcon />}>
                            Ask AI
                        </Button>
                    )}
                    {helpContent && (
                        <Button onClick={() => setShowDrawer(true)} size="small" icon={<InfoCircleTwoTone />}>
                            How To Use
                        </Button>
                    )}
                </div>
            </div>
            <div id="page-layout-content-wrapper" className="flex size-full flex-1 flex-col overflow-auto">
                <div id="page-layout-content-area" className="flex size-full flex-1 flex-col overflow-auto">
                    <div className={mergeClassNames("relative flex size-full flex-1 flex-col gap-2 overflow-auto", border ? "rounded-md border border-zinc-200 p-4" : "")}>
                        <ErrorBoundary>
                            {mainContent({
                                setBreadcrumbNavigationItems,
                            })}
                        </ErrorBoundary>
                    </div>
                </div>
                {helpContent && (
                    <Drawer title="How To Use" open={showDrawer} onClose={() => setShowDrawer(false)}>
                        <ErrorBoundary>{helpContent}</ErrorBoundary>
                    </Drawer>
                )}
                {askAIContent &&
                    (AskAIWrapper ? (
                        <AskAIWrapper>
                            <Drawer
                                title={askAITitle ?? "Ask AI"}
                                open={showChatDrawer}
                                onClose={() => setShowChatDrawer(false)}
                                styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
                            >
                                {askAIContent}
                            </Drawer>
                        </AskAIWrapper>
                    ) : (
                        <Drawer
                            title={askAITitle ?? "Ask AI"}
                            open={showChatDrawer}
                            onClose={() => setShowChatDrawer(false)}
                            styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
                        >
                            {askAIContent}
                        </Drawer>
                    ))}
                {assistantContent && (
                    <Drawer
                        title={
                            <span className="flex items-center gap-2 text-zinc-900">
                                <span className="text-[#001D3D]">
                                    <AssistantSparkleIcon />
                                </span>
                                {assistantTitle ?? "Assistant"}
                            </span>
                        }
                        open={showAssistantDrawer}
                        onClose={() => {
                            setShowAssistantDrawer(false);
                            assistantOnClose?.();
                        }}
                        mask={false}
                        width={assistantExpanded ? "90%" : 420}
                        extra={
                            <Button
                                type="text"
                                size="small"
                                icon={assistantExpanded ? <ShrinkOutlined /> : <ExpandOutlined />}
                                onClick={() => setAssistantExpanded((v) => !v)}
                                title={assistantExpanded ? "Shrink" : "Expand"}
                            />
                        }
                        styles={{
                            header: { backgroundColor: "#fafafa", borderBottom: "1px solid #e4e4e7" },
                            body: { padding: 0, backgroundColor: "#fafafa", display: "flex", flexDirection: "column" },
                            wrapper: { backgroundColor: "#fafafa" },
                        }}
                    >
                        <ErrorBoundary>{assistantContent}</ErrorBoundary>
                    </Drawer>
                )}
            </div>
        </div>
    );
}
